import { inject, Injectable } from '@angular/core';
import { MatchRequest, Notification } from '@models';
import { combineLatest, map, shareReplay, switchMap } from 'rxjs';
import { ClubManagerService } from './club-manager.service';
import { MatchRequestService } from './match-request.service';
import { NotificationsUserService } from './notifications-user.service';
import { TransferService } from './transfer.service';
import { UserService } from './current-user.service';

/** Documento de notificación listado en "Anteriores" con su marcador. */
export interface NotificationItem {
  notification: Notification;
  /** Muestra la campanita roja y cuenta en el badge. */
  unread: boolean;
}

/**
 * Aggregates every "pending" source a user can have (match date-change
 * requests, incoming transfers and unread admin notifications) into a single
 * unread signal. Used by the header to show the red dot and by the
 * notifications page to build its "Pendientes" (actions) and "Anteriores"
 * (notification documents) tabs.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsBadgeService {
  private readonly userService = inject(UserService);
  private readonly matchRequestService = inject(MatchRequestService);
  private readonly clubManagerService = inject(ClubManagerService);
  private readonly transferService = inject(TransferService);
  private readonly notificationsUserService = inject(NotificationsUserService);

  /**
   * Solicitudes de cambio de fecha pendientes que el usuario puede responder:
   * las dirigidas a equipos que delega y, si es gestor, las dirigidas a
   * cualquier equipo de su club. Un usuario delegado Y gestor recibiría la
   * misma solicitud por las dos vías: se deduplica por `id`.
   */
  readonly matchRequests$ = this.userService.isManager$.pipe(
    switchMap((isManager) =>
      isManager
        ? combineLatest([
            this.matchRequestService.myRequests$,
            this.clubManagerService.myRequests$,
          ]).pipe(
            map(([asDelegate, asManager]) => {
              const byId = new Map<string, MatchRequest>();
              [...asDelegate, ...asManager].forEach((r) => byId.set(r.id, r));
              return [...byId.values()];
            }),
          )
        : this.matchRequestService.myRequests$,
    ),
    shareReplay(1),
  );

  readonly transfers$ = this.transferService.myPlayersTransfersRequests$;

  /**
   * Acciones que esperan respuesta del usuario (pestaña "Pendientes"):
   * solicitudes de cambio de fecha pendientes + traspasos por responder.
   * Los documentos de notificación nunca cuentan aquí: son informativos.
   */
  readonly pendingActionsCount$ = combineLatest([
    this.matchRequests$,
    this.transfers$,
  ]).pipe(
    map(([requests, transfers]) => requests.length + transfers.length),
    shareReplay(1),
  );

  /**
   * Documentos de notificación para la pestaña "Anteriores" (leídos y no
   * leídos, en el orden `createdAt desc` de la consulta), con el marcador de
   * no leído ya resuelto en `unread`.
   *
   * Avisos de "solicitud de cambio de fecha recibida" (`type: 'MATCH_REQUEST'`
   * con `requestId`), según su solicitud siga o no pendiente en
   * `matchRequests$`:
   * - `covered`: la solicitud se muestra como tarjeta en Pendientes → el aviso
   *   se oculta (ni en Anteriores ni en el contador) para no duplicarla.
   * - `stale`: la solicitud ya no está pendiente para el usuario (respondida o
   *   borrada) → el aviso se lista en Anteriores sin marcador de no leído y sin
   *   contar en el badge: la acción que anunciaba ya está resuelta.
   * Los avisos antiguos sin `requestId` no se tocan (cuentan según `read`).
   */
  readonly notificationItems$ = combineLatest([
    this.matchRequests$,
    this.notificationsUserService.notifications$,
  ]).pipe(
    map(([requests, notifications]) => {
      const pendingIds = new Set(requests.map((r) => r.id));
      const items: NotificationItem[] = [];
      for (const n of notifications) {
        if (n.type === 'MATCH_REQUEST' && n.requestId) {
          if (pendingIds.has(n.requestId)) continue; // covered por la tarjeta
          items.push({ notification: n, unread: false }); // stale
        } else {
          items.push({ notification: n, unread: !n.read });
        }
      }
      return items;
    }),
    shareReplay(1),
  );

  /** Documentos de notificación visibles y no leídos (contador de Anteriores). */
  readonly unreadNotificationsCount$ = this.notificationItems$.pipe(
    map((items) => items.filter((i) => i.unread).length),
    shareReplay(1),
  );

  /**
   * Badge global (header y perfil): acciones pendientes + avisos no leídos.
   * Los avisos ocultos por su tarjeta (`covered`) no se cuentan, así que una
   * solicitud pendiente suma 1, no 2.
   */
  readonly unreadCount$ = combineLatest([
    this.pendingActionsCount$,
    this.unreadNotificationsCount$,
  ]).pipe(
    map(([actions, unread]) => actions + unread),
    shareReplay(1),
  );

  readonly hasUnread$ = this.unreadCount$.pipe(
    map((count) => count > 0),
    shareReplay(1),
  );
}
